import type { IMenuSection } from "@fondationcss/ui";

/**
 * Contrôle d'accès des menus.
 *
 * Sans `module`/`permissions`, le filtre du SDK Fondation laisse passer toutes
 * les entrées : un utilisateur sans aucun droit voyait donc l'intégralité de la
 * navigation, puis récupérait des erreurs au chargement de chaque page.
 *
 * Les valeurs ci-dessous doivent rester alignées sur les modules .NET
 * (`BaseModule.Name` et `BaseModule.Permissions`). Chaque entrée est gardée par
 * les permissions exigées par l'appel effectué au chargement de la page.
 *
 * Attention à la forme des identifiants, les deux ne suivent pas la même règle :
 * `me.modules` contient le nom nu du module (« Arkiv.Doc »), tandis que
 * `me.permissions` contient des noms pleinement qualifiés
 * (« Arkiv.Doc.Administrateur »). Utiliser ici le nom nu d'une permission la
 * rendrait introuvable et masquerait le menu à un utilisateur pourtant habilité.
 */
const MODULE = {
  EFVP: 'Arkiv.Efvp',
  MCN: 'Arkiv.Mcn',
  ARCHIVES: 'Arkiv.Archives',
  CAI: 'Arkiv.Cai',
  DOC: 'Arkiv.Doc',
  SIGNATURE: 'Arkiv.Signature',
} as const;

/** Permission pleinement qualifiée, telle que stockée par la Fondation. */
const perm = (module: string, name: string) => `${module}.${name}`;

/** Lecture du module documentaire : tous les niveaux, du plus large au plus restreint. */
const DOC_READ = ['Administer', 'Manage', 'Write', 'Read'].map(p => perm(MODULE.DOC, p));
/** Administration du module documentaire (référentiels : plans, règles, types, sources). */
const DOC_ADMIN = [perm(MODULE.DOC, 'Administer')];
/** Registre Loi 25 : réservé aux profils de gestion. */
const DOC_REGISTRY = ['Administer', 'Manage'].map(p => perm(MODULE.DOC, p));
/** Modules de conformité : une seule permission déclarée côté serveur. */
const EFVP_READ = [perm(MODULE.EFVP, 'Manage')];
const MCN_READ = [perm(MODULE.MCN, 'Manage')];
const ARCHIVES_READ = [perm(MODULE.ARCHIVES, 'Manage')];
const CAI_READ = [perm(MODULE.CAI, 'Manage')];
/** Module de signature : permissions exprimées en verbes. */
const SIGNATURE_READ = ['Manage', 'Send', 'Sign', 'Read'].map(p => perm(MODULE.SIGNATURE, p));
/** Les abonnements ouvrent une sortie de données et restent réservés aux gestionnaires. */
const SIGNATURE_MANAGE = [perm(MODULE.SIGNATURE, 'Manage')];

export const menus: IMenuSection[] = [
  {
    title: 'Conformité',
    icon: 'shield-halved',
    iconOnly: true,
    groups: [
      { title: 'EFVP / Loi 25', icon: 'shield-halved', to: '/efvp', module: MODULE.EFVP, permissions: EFVP_READ },
      { title: 'MCN', icon: 'lock', to: '/mcn', module: MODULE.MCN, permissions: MCN_READ },
      { title: 'Archives', icon: 'box-archive', to: '/archives', module: MODULE.ARCHIVES, permissions: ARCHIVES_READ },
      { title: 'CAI', icon: 'scale-balanced', to: '/cai', module: MODULE.CAI, permissions: CAI_READ },
    ],
  },
  {
    title: 'Documents',
    icon: 'folder-tree',
    iconOnly: true,
    module: MODULE.DOC,
    permissions: DOC_READ,
    groups: [
      { title: 'Voûtes', icon: 'vault', to: '/doc/vaults', module: MODULE.DOC, permissions: DOC_READ },
      { title: 'Gels légaux', icon: 'snowflake', to: '/doc/legal-holds', module: MODULE.DOC, permissions: DOC_READ },
      { title: 'Dispositions', icon: 'gavel', to: '/doc/dispositions', module: MODULE.DOC, permissions: DOC_READ },
      { title: 'Registre Loi 25', icon: 'user-shield', to: '/doc/privacy-registry', module: MODULE.DOC, permissions: DOC_REGISTRY },
    ],
  },
  {
    title: 'Signatures',
    icon: 'signature',
    iconOnly: true,
    module: MODULE.SIGNATURE,
    permissions: SIGNATURE_READ,
    // La barre latérale de la Fondation n'affiche que cinq entrées par section
    // faute d'indication contraire : sans ce plafond relevé, les dernières —
    // « Ma bibliothèque » et les abonnements — disparaîtraient sans le dire.
    maxGroups: 7,
    groups: [
      { title: 'Signatures', icon: 'file-signature', to: '/signatures', module: MODULE.SIGNATURE, permissions: SIGNATURE_READ },
      { title: 'Gabarits', icon: 'layer-group', to: '/signatures/gabarits', module: MODULE.SIGNATURE, permissions: SIGNATURE_READ },
      { title: 'Envois en lot', icon: 'envelopes-bulk', to: '/signatures/envois-lot', module: MODULE.SIGNATURE, permissions: SIGNATURE_READ },
      { title: 'Destinataires', icon: 'address-book', to: '/signatures/destinataires', module: MODULE.SIGNATURE, permissions: SIGNATURE_READ },
      { title: 'Groupes de destinataires', icon: 'users', to: '/signatures/destinataires/groupes', module: MODULE.SIGNATURE, permissions: SIGNATURE_READ },
      { title: 'Ma bibliothèque', icon: 'draw-polygon', to: '/signatures/bibliotheque', module: MODULE.SIGNATURE, permissions: SIGNATURE_READ },
      { title: 'Abonnements sortants', icon: 'tower-broadcast', to: '/signatures/abonnements', module: MODULE.SIGNATURE, permissions: SIGNATURE_MANAGE },
    ],
  },
  {
    title: 'Administration',
    icon: 'sliders',
    iconOnly: true,
    module: MODULE.DOC,
    permissions: DOC_ADMIN,
    groups: [
      { title: 'Voûtes (admin)', icon: 'vault', to: '/doc/admin/vaults', module: MODULE.DOC, permissions: DOC_ADMIN },
      { title: 'Sources de stockage', icon: 'database', to: '/doc/admin/storage-sources', module: MODULE.DOC, permissions: DOC_ADMIN },
      { title: 'Plans de classification', icon: 'sitemap', to: '/doc/admin/classification-plans', module: MODULE.DOC, permissions: DOC_ADMIN },
      { title: 'Règles de conservation', icon: 'clock', to: '/doc/admin/retention-rules', module: MODULE.DOC, permissions: DOC_ADMIN },
      { title: 'Types de documents', icon: 'file-lines', to: '/doc/admin/document-types', module: MODULE.DOC, permissions: DOC_ADMIN },
    ],
  },
]
